/**
 * Responsibility: Implement persistence check behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import { totalMinFromClock } from '../../temporal'
import type { SupervisorCheckFn, SupervisorIncidentSeed } from "@anyquest/shared-core"

const MAX_WORLD_LOG = 30
const MAX_HISTORY = 500

export const persistenceCheck: SupervisorCheckFn = (state) => {
  const incidents: SupervisorIncidentSeed[] = []

  const timestamp = Number(state.meta?.timestamp)
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    incidents.push({
      code: 'persistence_meta_timestamp_invalid',
      domain: 'persistence',
      severity: 'medium',
      confidence: 0.99,
      message: 'State meta timestamp is invalid.',
      remediationPlan: 'Set timestamp to current epoch milliseconds.',
      context: { timestamp: state.meta?.timestamp },
    })
  }

  if ((state.worldLog?.length ?? 0) > MAX_WORLD_LOG) {
    incidents.push({
      code: 'persistence_world_log_oversized',
      domain: 'persistence',
      severity: 'low',
      confidence: 0.99,
      message: `World log exceeds cap (${state.worldLog.length}/${MAX_WORLD_LOG}).`,
      remediationPlan: 'Trim world log FIFO to configured max size.',
      context: { count: state.worldLog.length, max: MAX_WORLD_LOG },
    })
  }

  if ((state.history?.length ?? 0) > MAX_HISTORY) {
    incidents.push({
      code: 'persistence_history_oversized',
      domain: 'persistence',
      severity: 'low',
      confidence: 0.97,
      message: `History exceeds soft cap (${state.history.length}/${MAX_HISTORY}).`,
      remediationPlan: 'Trim oldest history entries to bounded retention.',
      context: { count: state.history.length, max: MAX_HISTORY },
    })
  }

  const nowMin = totalMinFromClock(state.world.clock)
  if (Number.isFinite(state.world?.lastTick) && (state.world.lastTick ?? 0) > nowMin + 24 * 60) {
    incidents.push({
      code: 'persistence_world_last_tick_ahead_of_clock',
      domain: 'persistence',
      severity: 'low',
      confidence: 0.95,
      message: 'world.lastTick is significantly ahead of current clock.',
      remediationPlan: 'Clamp lastTick to current world minute.',
      context: { lastTick: state.world.lastTick, nowMin },
    })
  }

  return { passed: incidents.length === 0, incidents }
}
